import { useState } from 'react';
import {
  createFinanceContactRequest,
  ContactKind,
  ContactType,
  OrganizationType,
  organizationTypeLabel,
} from '@signalvote/shared';
import type { CreateFinanceContactRequest } from '@signalvote/shared';
import { AddressAutocomplete, canonicalAddressRequestFields } from '@/components/finance/shared/classification';
import type { CanonicalAddressValue } from '@/components/finance/shared/classification';

/**
 * The subtypes a new organization may take: the jurisdiction's declared set, plus "Other organization"
 * so one that fits no declared subtype can still be saved. The server's source bans key on the
 * subtype, so an organization is never created without one.
 */
export function creatableOrganizationTypes(supported: readonly OrganizationType[]): readonly OrganizationType[] {
  return supported.includes(OrganizationType.OtherOrganization)
    ? supported
    : [...supported, OrganizationType.OtherOrganization];
}

function splitPersonName(name: string): { first: string; last: string } {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return { first: words[0] ?? '', last: '' };
  return { first: words.slice(0, -1).join(' '), last: words[words.length - 1] };
}

export interface InlineContactFormProps {
  contactType: ContactType;
  /** Text the treasurer already typed (a search box), seeded as both the person and the organization name. */
  initialName?: string;
  showEmployerOccupation: boolean;
  /** The jurisdiction's declared subtypes (`ComplianceProfileDto.supportedOrganizationTypes`). */
  supportedOrganizationTypes: readonly OrganizationType[];
  /** Mapbox address search in place of the plain fields; a picked suggestion rides on the request as the canonical address. */
  addressAutocomplete?: boolean;
  isSaving: boolean;
  onSave: (request: CreateFinanceContactRequest) => Promise<void>;
  onCancel: () => void;
}

export function InlineContactForm({
  contactType,
  initialName = '',
  showEmployerOccupation,
  supportedOrganizationTypes,
  addressAutocomplete = false,
  isSaving,
  onSave,
  onCancel,
}: InlineContactFormProps) {
  const [contactKind, setContactKind] = useState<ContactKind>(ContactKind.Individual);
  const [firstName, setFirstName] = useState(() => splitPersonName(initialName).first);
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState(() => splitPersonName(initialName).last);
  const [nameSuffix, setNameSuffix] = useState('');
  const [organizationName, setOrganizationName] = useState(() => initialName.trim().split(/\s+/).filter(Boolean).join(' '));
  const [organizationType, setOrganizationType] = useState<OrganizationType | ''>('');
  const [street, setStreet] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [canonical, setCanonical] = useState<CanonicalAddressValue | null>(null);
  const [employer, setEmployer] = useState('');
  const [occupation, setOccupation] = useState('');
  const [employmentLocation, setEmploymentLocation] = useState('');

  const isIndividual = contactKind === ContactKind.Individual;
  const organizationTypeOptions = creatableOrganizationTypes(supportedOrganizationTypes);
  const canSave =
    !isSaving &&
    (isIndividual
      ? firstName.trim() !== '' && lastName.trim() !== ''
      : organizationName.trim() !== '' && organizationType !== '');

  const handleSave = () => {
    if (!canSave) return;
    onSave(createFinanceContactRequest({
      contactType,
      contactKind,
      firstName: isIndividual ? firstName.trim() : null,
      middleName: isIndividual ? (middleName.trim() || null) : null,
      lastName: isIndividual ? lastName.trim() : null,
      nameSuffix: isIndividual ? (nameSuffix.trim() || null) : null,
      organizationName: isIndividual ? null : organizationName.trim(),
      organizationType: isIndividual || organizationType === '' ? null : organizationType,
      address: street.trim() || null,
      addressLine2: addressLine2.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      zipCode: zip.trim() || null,
      email: null,
      phone: null,
      employer: employer.trim() || null,
      occupation: occupation.trim() || null,
      employmentLocation: employmentLocation.trim() || null,
      ...(canonical && canonicalAddressRequestFields({
        canonicalAddressLine1: canonical.line1,
        canonicalAddressLine2: canonical.line2 || null,
        canonicalCity: canonical.city,
        canonicalState: canonical.state,
        canonicalZip: canonical.zip,
      })),
    }));
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div>
        <label htmlFor="inline-contact-kind" className="block text-sm font-medium mb-1">Kind</label>
        <select
          id="inline-contact-kind"
          value={contactKind}
          onChange={(e) => setContactKind(e.target.value as ContactKind)}
          className="w-full rounded-md border px-3 py-2 text-sm"
        >
          <option value={ContactKind.Individual}>Individual</option>
          <option value={ContactKind.Organization}>Organization</option>
        </select>
      </div>

      {isIndividual ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="inline-contact-first-name" className="block text-sm font-medium mb-1">First Name *</label>
            <input
              id="inline-contact-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="inline-contact-middle-name" className="block text-sm font-medium mb-1">Middle</label>
            <input
              id="inline-contact-middle-name"
              type="text"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="inline-contact-last-name" className="block text-sm font-medium mb-1">Last Name *</label>
            <input
              id="inline-contact-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="inline-contact-suffix" className="block text-sm font-medium mb-1">Suffix</label>
            <input
              id="inline-contact-suffix"
              type="text"
              value={nameSuffix}
              onChange={(e) => setNameSuffix(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
              placeholder="Jr., III"
            />
          </div>
        </div>
      ) : (
        <div>
          <label htmlFor="inline-contact-org-name" className="block text-sm font-medium mb-1">Organization Name *</label>
          <input
            id="inline-contact-org-name"
            type="text"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
            autoFocus
          />
          <label htmlFor="inline-contact-org-type" className="block text-sm font-medium mb-1 mt-2">Organization type *</label>
          <select
            id="inline-contact-org-type"
            data-testid="inline-contact-org-type"
            value={organizationType}
            onChange={(e) => setOrganizationType(e.target.value as OrganizationType | '')}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="">Select a type</option>
            {organizationTypeOptions.map((value) => (
              <option key={value} value={value}>{organizationTypeLabel(value)}</option>
            ))}
          </select>
        </div>
      )}

      {addressAutocomplete ? (
        <AddressAutocomplete
          idPrefix="inline-contact"
          value={{ address: street, addressLine2, city, state, zip }}
          onChange={(next) => {
            setStreet(next.address);
            setAddressLine2(next.addressLine2);
            setCity(next.city);
            setState(next.state);
            setZip(next.zip);
          }}
          onCanonicalChange={setCanonical}
        />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label htmlFor="inline-contact-street" className="block text-sm font-medium mb-1">Street</label>
            <input id="inline-contact-street" type="text" value={street} onChange={(e) => setStreet(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <label htmlFor="inline-contact-address2" className="block text-sm font-medium mb-1">Address line 2</label>
            <input id="inline-contact-address2" data-testid="inline-contact-address2" type="text" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} placeholder="Apt, suite, floor (optional)" className="w-full rounded-md border px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor="inline-contact-city" className="block text-sm font-medium mb-1">City</label>
            <input id="inline-contact-city" type="text" value={city} onChange={(e) => setCity(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2">
            <div className="w-16">
              <label htmlFor="inline-contact-state" className="block text-sm font-medium mb-1">State</label>
              <input id="inline-contact-state" type="text" value={state} onChange={(e) => setState(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" maxLength={2} />
            </div>
            <div className="flex-1">
              <label htmlFor="inline-contact-zip" className="block text-sm font-medium mb-1">Zip</label>
              <input id="inline-contact-zip" type="text" value={zip} onChange={(e) => setZip(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" />
            </div>
          </div>
        </div>
      )}

      {showEmployerOccupation && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="inline-contact-employer" className="block text-sm font-medium mb-1">Employer</label>
            <input id="inline-contact-employer" type="text" value={employer} onChange={(e) => setEmployer(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor="inline-contact-occupation" className="block text-sm font-medium mb-1">Occupation</label>
            <input id="inline-contact-occupation" type="text" value={occupation} onChange={(e) => setOccupation(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <label htmlFor="inline-contact-employment-location" className="block text-sm font-medium mb-1">Employment location</label>
            <input id="inline-contact-employment-location" type="text" value={employmentLocation} onChange={(e) => setEmploymentLocation(e.target.value)} placeholder="City, State" className="w-full rounded-md border px-3 py-2 text-sm" />
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          aria-label="Save contact"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save contact'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
